import { z } from 'zod';

// ─── Access Group Event Payloads ────────────────────────────────────
// Zod schemas for every access-group-domain event.
// These represent the *command* shape published by the API (BFF).
// The users service consumes these and performs the actual DB writes.

export const AccessCreatedPayload = z.object({
    accessId: z.string().min(1),
    name: z.string().min(1),
    tenantId: z.string().min(1),
    description: z.string().optional(),
    permissions: z.array(z.object({
        type: z.enum(['PAGE', 'ACTION']),
        method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
        route: z.string().min(1),
    })).optional(),
});

export type AccessCreatedPayload = z.infer<typeof AccessCreatedPayload>;

export const AccessBulkCreatedPayload = z.object({
    creates: z.array(AccessCreatedPayload).min(1).max(50),
});

export type AccessBulkCreatedPayload = z.infer<typeof AccessBulkCreatedPayload>;

export const AccessUpdatedPayload = z.object({
    accessId: z.string().min(1),
    changes: z.object({
        name: z.string().min(1).optional(),
        description: z.string().optional(),
    }).refine((c) => Object.keys(c).length > 0, { message: 'At least one change is required' }),
    previousName: z.string().optional(),
});

export type AccessUpdatedPayload = z.infer<typeof AccessUpdatedPayload>;

export const AccessDeletedPayload = z.object({
    accessId: z.string().min(1),
    tenantId: z.string().min(1),
});

export type AccessDeletedPayload = z.infer<typeof AccessDeletedPayload>;

export const AccessRestoredPayload = z.object({
    accessId: z.string().min(1),
    tenantId: z.string().min(1),
    restoredBy: z.string(),
});

export type AccessRestoredPayload = z.infer<typeof AccessRestoredPayload>;

export const AccessPermissionsUpdatedPayload = z.object({
    accessId: z.string().min(1),
    permissions: z.array(z.object({
        type: z.enum(['PAGE', 'ACTION']),
        method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']),
        route: z.string().min(1),
    })).min(1),
});

export type AccessPermissionsUpdatedPayload = z.infer<typeof AccessPermissionsUpdatedPayload>;

/**
 * Follow-up to a role permission change: re-exchange these users' tokens.
 * Published after the permissions are written, so a redelivery only marks
 * tokens again and can never replay a permission write.
 */
export const AccessHoldersRefreshRequestedPayload = z.object({
    userIds: z.array(z.string().min(1)).min(1),
});

export type AccessHoldersRefreshRequestedPayload = z.infer<typeof AccessHoldersRefreshRequestedPayload>;
